import React, { ReactNode, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  UncontrolledTooltip,
  Badge,
  Toast,
  ToastBody,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Input,
  ToastHeader,
} from 'reactstrap'
import { Picture } from 'react-responsive-picture'
import cx from 'classnames'

import '../style/index.scss'
import { getCapabilities, getMediaHref } from '../core/helpers'
import posthog from '../api/posthog'
import LinkToMemberships from './LinkToMemberships'
import FeaturePromoTooltip from './FeaturePromoTooltip'
import {
  DEFAULT_CAPABILITIES,
  editLanguages,
  ECHOES_LOGO_SOURCES,
} from '../constants/index'
import { useAppDispatch, useTypedSelector } from '../store/store'
import { useGetMySubscriptionsQuery, util } from '../store/apiSlice'
import {
  removeToast,
  setLang,
  setUserCapabilities,
  toggleMapFullscreen,
  toggleMapVisible,
} from '../store/creatorSlice'
import { clearToken } from '../store/authSlice'
import { useGetMyUserQuery } from '../store/userApiSlice'

interface LayoutProps {
  children?: ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation()
  const [isMembershipUpgradeActive, setIsMembershipUpgradeActive] =
    useState(false) //hide membership toast // TODO: move it to redux
  const [isNavbarOpen, setIsNavbarOpen] = useState(false)

  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { data: self } = useGetMyUserQuery({})
  const { data: subscriptions } = useGetMySubscriptionsQuery({})
  // const { data: profiles, isFetching: isFetchingMyProfiles } = useGetMyProfilesQuery({})

  const mapVisible = useTypedSelector((state) => state.creator.mapVisible)
  const mapFullscreen = useTypedSelector((state) => state.creator.mapFullscreen)
  const lang = useTypedSelector((state) => state.creator.lang)
  const showNewsletterPrompt = useTypedSelector(
    (state) => state.creator.showNewsletterPrompt
  )
  const toasts = useTypedSelector((state) => state.creator.toasts)
  const isAdmin = useMemo(() => Boolean(self && self.role === 'admin'), [self])

  const echoesBranch = useMemo(() => {
    let b: string = import.meta.env.VITE_GIT_BRANCH
    return ['alpha', 'beta', 'dev'].indexOf(b) > -1 ? b : null
  }, [])
  const capabilities = useMemo<IUserCapabilities>(() => {
    if (self && subscriptions) {
      return getCapabilities(self, subscriptions)
    } else {
      return DEFAULT_CAPABILITIES
    }
  }, [self, subscriptions])

  useEffect(() => {
    dispatch(setUserCapabilities(capabilities))
  }, [capabilities, dispatch])

  const profilePhoto = useMemo(
    () =>
      self &&
      (self.media ? getMediaHref(self.media, 'profile-photo') : undefined),
    [self]
  )
  const activeSubscription = useMemo(
    () => subscriptions?.find((sub) => sub.active),
    [subscriptions]
  )
  const hasSubscription = useMemo(
    () => activeSubscription !== undefined,
    [activeSubscription]
  )

  const handleClickBrand = () => {
    navigate('/')
  }

  const requestSupport = async () => {
    posthog.capture('requested_support', {
      slug: params?.slug || '',
      collection: params?.slug || '',
    })

    const components: Record<string, string> = {
      email: self?.email ?? '',
      'collection-url': params?.slug ?? '',
    }
    const qs = Object.entries(components)
      .map(
        (entry) =>
          `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`
      )
      .join('&')
    window.open(`${import.meta.env.VITE_WEBSITE_URI}/creator-support?${qs}`)
  }

  const upgradeMembership = () => {
    navigate('/subscription/purchase')
    setIsMembershipUpgradeActive(false)
  }

  const dismissMembershipUpgrade = () => {
    setIsMembershipUpgradeActive(false)
  }

  const handleSelectLanguage = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLang(ev.currentTarget.value))
  }

  const handleClickLogout = () => {
    // @TODO: reinstate reset app config
    // resetAppConfig(dispatch)
    dispatch(clearToken())
    dispatch(util.resetApiState())
  }

  const handleToggleMap = () => {
    dispatch(toggleMapVisible())
  }

  const handleToggleMapFullscreen = () => {
    dispatch(toggleMapFullscreen())
  }

  const handleOpenEchoesDocs = () => {
    window.open(
      import.meta.env.VITE_ECHOES_DOCS_URL,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleOpenEchoesForum = () => {
    window.open(
      import.meta.env.VITE_ECHOES_FORUM_URL,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleOnSubmit = (values: any) => {
    // @TODO: finish mailchimp form submit
    console.info(values)
  }

  const handleDismissToast = (id: string) => {
    dispatch(removeToast(id))
  }

  return (
    <div className="h-100 d-flex flex-column">
      <nav className="navbar navbar-expand-md px-3 sticky-top navbar-dark shadow">
        <div className="navbar-brand" onClick={handleClickBrand}>
          <Picture sources={ECHOES_LOGO_SOURCES} className="header-logo" />
          {hasSubscription && activeSubscription && activeSubscription.type && (
            <Badge
              color="accent"
              // size
              className="text-dark fw-light ms-1 align-top"
            >
              {activeSubscription?.type.toUpperCase()}
            </Badge>
          )}
        </div>
        <div className="d-flex">
          <div
            className="btn btn-dark text-white p-3 me-2 d-flex d-sm-none justify-content-center align-items-center"
            aria-label="Toggle map"
            onClick={() => handleToggleMapFullscreen()}
          >
            <i className="bi-map"></i>
          </div>
          <button
            className="navbar-toggler text-white bg-dark p-2 d-flex d-sm-none justify-content-center align-items-center"
            aria-label="Toggle navigation"
            id="navbarToggler"
            type="button"
            onClick={() => setIsNavbarOpen(!isNavbarOpen)}
          >
            <i
              className="toggler-icon bi-list m-1"
              style={{ fontSize: 20 }}
            ></i>
          </button>
        </div>
        <div
          id="mainNavbar"
          className={cx('collapse navbar-collapse justify-content-end', {
            show: isNavbarOpen,
          })}
        >
          <ul className="navbar-nav ml-auto align-items-center">
            {isAdmin && (
              <UncontrolledDropdown className="nav-item">
                <DropdownToggle nav caret>
                  {t('admin')}
                </DropdownToggle>
                <DropdownMenu className="py-3">
                  <DropdownItem
                    className={cx({
                      active: location.pathname.startsWith('/admin/walks'),
                    })}
                    onClick={() => navigate('/admin/walks')}
                  >
                    {t('walks')}
                  </DropdownItem>
                  <DropdownItem
                    className={cx({
                      active: location.pathname.startsWith(
                        '/admin/subscriptions'
                      ),
                    })}
                    onClick={() => navigate('/admin/subscriptions')}
                  >
                    {t('memberships_label')}
                  </DropdownItem>
                  <DropdownItem
                    className={cx({
                      active: location.pathname.startsWith('/admin/profiles'),
                    })}
                    onClick={() => navigate('/admin/profiles')}
                  >
                    {t('profiles_label')}
                  </DropdownItem>
                  <DropdownItem
                    className={cx({
                      active: location.pathname.startsWith('/admin/requests'),
                    })}
                    onClick={() => navigate('/admin/requests')}
                  >
                    {t('requests')}
                  </DropdownItem>
                  <DropdownItem
                    className={cx({
                      active: location.pathname.startsWith('/admin/users'),
                    })}
                    onClick={() => navigate('/admin/users')}
                  >
                    {t('users')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}

            <li className="nav-item">
              <a
                className={cx('nav-link', {
                  active: location.pathname.startsWith('/walk'),
                })}
                onClick={() => navigate('/walks')}
              >
                {t('my_walks')}
              </a>
            </li>

            <li className="nav-item">
              <a
                className={cx('nav-link', {
                  active: location.pathname.startsWith('/profiles'),
                })}
                onClick={() => navigate('/profiles')}
              >
                {t('profiles_label')}
              </a>
            </li>

            <li className="nav-item">
              <a
                className={cx('nav-link d-block d-sm-none', {
                  active: location.pathname.startsWith('/account'),
                })}
                onClick={() => navigate('/account')}
              >
                {t('account')}
              </a>
            </li>

            {capabilities.changeLanguage && (
              <li className="nav-item">
                <select
                  id="changeLanguageGlobal"
                  className="form-select form-select-sm"
                  value={lang}
                  onChange={handleSelectLanguage}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '0 none',
                    maxWidth: '100px'
                  }}
                >
                  {editLanguages.map((eL, index) => (
                    <option key={index} value={eL.value}>
                      {eL.label}
                    </option>
                  ))}
                </select>
              </li>
            )}

            <li className="nav-item d-none d-sm-block">
              <a
                id="navbarMapToggle"
                className="nav-link"
                onClick={() => handleToggleMap()}
              >
                <i className="d-none d-sm-block bi-map"></i>
                <span className="d-block d-sm-none">
                  {mapVisible ? t('hide') : t('show')} {t('map')}
                </span>
              </a>
              <UncontrolledTooltip placement="bottom" target="navbarMapToggle">
                {t('toggle_map')}
              </UncontrolledTooltip>
            </li>

            <li
              className={cx('nav-item', {
                'd-none d-sm-block': !mapVisible,
              })}
            >
              <div>
                <a
                  id="navbarMapFullscreenToggle"
                  className="nav-link"
                  onClick={() => handleToggleMapFullscreen()}
                >
                  <i
                    className={cx('d-none d-sm-block', {
                      'bi-fullscreen-exit': mapFullscreen,
                      'bi-fullscreen': !mapFullscreen,
                    })}
                  ></i>
                  <span className="d-block d-sm-none">
                    {mapFullscreen ? t('exit') : t('show')}{' '}
                    {t('fullscreen_map')}
                  </span>
                </a>
                <UncontrolledTooltip
                  placement="bottom"
                  target="navbarMapFullscreenToggle"
                >
                  {t('fullscreen_map')}
                </UncontrolledTooltip>
              </div>
            </li>

            <UncontrolledDropdown className="nav-item dropdown d-none d-sm-block">
              <DropdownToggle nav className="d-flex">
                {profilePhoto ? (
                  <img src={profilePhoto} className="avatar avatar-xs" alt="" />
                ) : (
                  <>
                    <i
                      className={cx(
                        'd-none d-sm-block',
                        'bi-person-circle'
                      )}
                    ></i>
                    <span className="d-block d-sm-none">
                      {t('edit_profile')}
                    </span>
                  </>
                )}
              </DropdownToggle>

              <DropdownMenu className="bg-dark text-white">
                <DropdownItem>
                  <a className="nav-link" href="/account">
                    {t('account')}
                  </a>
                </DropdownItem>
                <DropdownItem
                  className="bg-transparent border-top border-secondary pt-2 px-4"
                  style={{ minWidth: 260 }}
                  tag="div"
                >
                  {activeSubscription ? (
                    <span className="text-white">
                      {activeSubscription.type.toUpperCase()} {t('membership')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary w-100 text-uppercase py-2"
                      onClick={() => upgradeMembership()}
                      style={{ fontSize: 12 }}
                    >
                      {t('upgrade_membership')}
                    </button>
                  )}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>

            {/* Support */}
            <li className="nav-item">
              <div className="nav-link">
                <FeaturePromoTooltip
                  target="requestSupportButtonDesktopTooltip"
                  text={
                    <LinkToMemberships
                      prompt={
                        <span>
                          To request dedicated support, check our{' '}
                          <a href="https://echoes.xyz/faq" target="_blank">
                            FAQ
                          </a>
                          , or
                        </span>
                      }
                    />
                  }
                  disabled={hasSubscription}
                  placement="bottom"
                >
                  <button
                    type="button"
                    className="btn btn-link text-secondary"
                    id="requestSupportButtonDesktopTooltip"
                    onClick={() => requestSupport()}
                    disabled={!hasSubscription}
                  >
                    <span className="d-block d-sm-none">
                      {t('request_support')}
                    </span>
                    <i className="d-none d-sm-block bi-envelope"></i>
                  </button>
                  {hasSubscription && (
                    <UncontrolledTooltip
                      placement="bottom"
                      target="requestSupportButtonDesktopTooltip"
                    >
                      {t('request_support')}
                    </UncontrolledTooltip>
                  )}
                </FeaturePromoTooltip>
              </div>
            </li>

            <li className="nav-item d-block d-sm-none my-2 w-100 px-4">
              {hasSubscription ? (
                <span
                  className="d-block text-center text-white border border-secondary w-100 py-2 text-uppercase"
                  style={{ fontSize: 12 }}
                >
                  {activeSubscription?.type.toUpperCase()} {t('membership')}
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary w-100 p-2 text-uppercase"
                  style={{ fontSize: 12 }}
                  onClick={upgradeMembership}
                >
                  {t('upgrade_membership')}
                </button>
              )}
            </li>

            <li className="nav-item d-none d-sm-block"></li>

            {/* FORUM */}
            {/* <li className="nav-item d-none d-sm-block">
              <a
                id="echoesForumTooltip"
                onClick={handleOpenEchoesForum}
                className="nav-link d-flex"
              >
                <i className="bi-people-fill"></i>
              </a>
              <UncontrolledTooltip
                placement="bottom"
                target="echoesForumTooltip"
              >
                Echoes Forum
              </UncontrolledTooltip>
            </li> */}

            {/* DOCS */}
            <li className="nav-item d-none d-sm-block">
              <a
                id="echoesDocsTooltip"
                className="nav-link d-flex"
                onClick={handleOpenEchoesDocs}
              >
                <i className="bi-question-circle"></i>
              </a>
              <UncontrolledTooltip
                placement="bottom"
                target="echoesDocsTooltip"
              >
                {t('echoes_docs')}
              </UncontrolledTooltip>
            </li>

            {/* LOGOUT */}
            <li className="nav-item">
              <a
                id="logoutButtonTooltip"
                className="nav-link d-flex"
                onClick={() => handleClickLogout()}
              >
                <i
                  className={cx('d-none d-sm-block', 'bi-box-arrow-right')}
                ></i>
                <span className="d-block d-sm-none">{t('sign_out')}</span>
              </a>
              <UncontrolledTooltip
                placement="bottom"
                target="logoutButtonTooltip"
              >
                {t('log_out')}
              </UncontrolledTooltip>
            </li>
          </ul>
        </div>
      </nav>
      <div className="h-100 flex-grow-1 flex-shrink-1 overflow-auto">
        {children}
      </div>

      <div className="d-flex align-items-center justify-content-between text-light px-3 py-1 small">
        <span className="flex-grow-1">
          {t('version')} {import.meta.env.PACKAGE_VERSION}{' '}
        </span>
        {echoesBranch && (
          <span className="badge text-bg-secondary mx-2">{echoesBranch}</span>
        )}
        <NavLink to="/changelog">{t('changelog')}</NavLink>
        {showNewsletterPrompt && (
          <form
            className="d-flex px-3 py-1 align-items-center"
            onSubmit={handleOnSubmit}
          >
            <div className="text-light pe-3">{t('subscribe_newsletter')}</div>
            <div className="">
              <div className="input-group input-group-sm">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('email_placeholder')}
                  aria-label="Username"
                  aria-describedby="emailInputLabel"
                />
                <button
                  className="btn btn-outline-secondary"
                  type="submit"
                  id="button-addon2"
                >
                  {t('subscribe')}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
      <Toast
        isOpen={!activeSubscription && isMembershipUpgradeActive}
        className="fixed-bottom m-3 bg-toast-dark px-1 py-1"
        style={{ maxWidth: 470 }}
      >
        <div className="toast-header bg-toast-dark text-white pt-0 pb-2 light-close-icon">
          <strong className="mr-auto"></strong>
          <button
            type="button"
            className="btn btn-link close"
            onClick={() => dismissMembershipUpgrade()}
          >
            <i
              className="bi-x"
              style={{ fontSize: '0.4rem' }}
            ></i>
          </button>
        </div>
        <ToastBody
          className="d-flex justify-content-center align-items-center pt-0 pb-4 mb-2"
          style={{ paddingLeft: 35, paddingRight: 38 }}
        >
          <div className="me-3">
            <i
              className="bi-info-circle mb-3 text-white"
              style={{ fontSize: 18 }}
            ></i>
          </div>
          <p className="mb-0 me-3 text-white">{t('unlock_features')}</p>
          <button
            type="button"
            className="btn btn-primary btn-sm py-1 px-4"
            onClick={() => upgradeMembership()}
            style={{ fontSize: 12 }}
          >
            {t('upgrade_membership')}
          </button>
        </ToastBody>
      </Toast>
      <div className="toast-container position-fixed bottom-0 end-0 p-3">
        {toasts.map((toast) => {
          return (
            <Toast key={toast.id} isOpen={toast.isOpen}>
              <ToastHeader
                icon={toast.icon}
                toggle={() => handleDismissToast(toast.id)}
              >
                {toast.title}
              </ToastHeader>
              <ToastBody>{toast.body}</ToastBody>
            </Toast>
          )
        })}
      </div>
    </div>
  )
}

export default Layout
